/**
 * Mock implementation of the report service for testing
 */
import type { 
  BudgetReport, 
  TaskBudgetReport, 
  IntervenantBudgetReport, 
  ApiResponse 
} from '@/types'
import { mockProjectService } from './mock-project.service'

export class MockReportService {
  /**
   * Get comprehensive budget consumption report for a project
   */
  async getBudgetConsumptionReport(projectId: string | number): Promise<ApiResponse<BudgetReport>> {
    console.log('Mock ReportService: getBudgetConsumptionReport called for project', projectId)
    return await mockProjectService.getBudgetConsumptionReport(projectId)
  }

  /**
   * Get task budget reports for a project
   */
  async getTaskBudgetReport(projectId: string | number): Promise<ApiResponse<TaskBudgetReport>> {
    console.log('Mock ReportService: getTaskBudgetReport called for project', projectId)
    return await mockProjectService.getTaskBudgetReport(projectId)
  }

  /**
   * Get intervenant budget reports for a project
   */
  async getIntervenantBudgetReport(projectId: string | number): Promise<ApiResponse<IntervenantBudgetReport>> {
    console.log('Mock ReportService: getIntervenantBudgetReport called for project', projectId)
    return await mockProjectService.getIntervenantBudgetReport(projectId)
  }

  /**
   * Get detailed budget information for a specific task
   */
  async getTaskBudgetDetails(projectId: string | number, taskId: string | number): Promise<any> {
    console.log('Mock ReportService: getTaskBudgetDetails called for project', projectId, 'task', taskId)

    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 400))

    const taskReportsResponse = await mockProjectService.getTaskBudgetReport(projectId)
    const taskReport = taskReportsResponse.data.tasks.find(task => task.taskId.toString() === taskId.toString())

    if (!taskReport) {
      throw new Error('Task not found')
    }

    return taskReport
  }

  /**
   * Export budget consumption report as PDF (mock)
   */
  async exportBudgetReportPDF(projectId: string | number): Promise<Blob> {
    console.log('Mock ReportService: exportBudgetReportPDF called for project', projectId)

    // Create a delay to simulate file generation
    await new Promise(resolve => setTimeout(resolve, 1500))

    // Create a mock PDF blob
    const mockPdfContent = `%PDF-1.4
1 0 obj
<<
/Type /Catalog
/Pages 2 0 R
>>
endobj

2 0 obj
<<
/Type /Pages
/Kids [3 0 R]
/Count 1
>>
endobj

3 0 obj
<<
/Type /Page
/Parent 2 0 R
/MediaBox [0 0 612 792]
/Contents 4 0 R
>>
endobj

4 0 obj
<<
/Length 44
>>
stream
BT
/F1 12 Tf
100 700 Td
(Mock Budget Report - Project ${projectId}) Tj
ET
endstream
endobj

xref
0 5
0000000000 65535 f 
0000000010 00000 n 
0000000053 00000 n 
0000000110 00000 n 
0000000190 00000 n 
trailer
<<
/Size 5
/Root 1 0 R
>>
startxref
285
%%EOF`

    return new Blob([mockPdfContent], { type: 'application/pdf' })
  }

  /**
   * Export budget consumption report as Excel (mock)
   */
  async exportBudgetReportExcel(projectId: string | number): Promise<Blob> {
    console.log('Mock ReportService: exportBudgetReportExcel called for project', projectId)

    // Create a delay to simulate file generation
    await new Promise(resolve => setTimeout(resolve, 1200))

    // Create a mock Excel blob (simplified CSV format)
    const reportResponse = await mockProjectService.getBudgetConsumptionReport(projectId)
    const report = reportResponse.data

    let csvContent = `Rapport de Consommation Budgétaire - ${report.projectName}\n\n`
    csvContent += `Généré le,${new Date().toISOString()}\n`
    csvContent += `Budget Total,${report.totalBudget}€\n`
    csvContent += `Budget Consommé,${report.consumedBudget}€\n`
    csvContent += `Budget Restant,${report.remainingBudget}€\n`
    csvContent += `Pourcentage Consommé,${report.consumptionPercentage}%\n\n`

    csvContent += `Détail des Catégories\n`
    csvContent += `Catégorie,Budget Alloué,Budget Consommé,Budget Restant,% Consommé\n`

    report.categories.forEach(category => {
      csvContent += `"${category.name}",${category.allocated}€,${category.spent}€,${category.remaining}€,${category.percentage}%\n`
    })

    return new Blob([csvContent], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
  }
}

// Create and export a singleton instance
export const mockReportService = new MockReportService()
export default mockReportService
